import React, { useEffect, useState } from 'react';
import '../../App.css';
import './MangaPage.css';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import MangaApiService from '../../services/MangaApiService';
import ApiService from '../../services/ApiService';
import MangaAccessNotice from './MangaAccessNotice';
import MangaStudioNav from './MangaStudioNav';
import { extractUploadMeta,
    getImageUploadErrorMessage,
    getTeamId,
    getRejectionReason,
    isRejectedStatus,
    joinLinks,
    resolveDonationKey,
    splitLinks,
    translateStatus,
    unwrapList,
    unwrapResponse,
    getMangaErrorMessage,
    translateTeamRole,
    translateAuditAction,
    formatAuditDetails,
} from './mangaStudioUtils';
import teamIcon from '../../assets/ic_profile.svg';

const isRoleActive = (value) => value === true || value === 'true' || value === 1 || value === '1';

const emptyTeamForm = {
    name: '',
    description: '',
    donationLinks: '',
    logoFileId: '',
    logoStorageBucket: '',
};

const LOGO_MAX_SIZE_BYTES = 3 * 1024 * 1024;
const TEAM_NAME_LIMIT = 128;

const MangaStudioTeamPage = () => {
    const [team, setTeam] = useState(null);
    const [teamForm, setTeamForm] = useState(emptyTeamForm);
    const [teamLoading, setTeamLoading] = useState(false);
    const [teamError, setTeamError] = useState(null);
    const [teamMessage, setTeamMessage] = useState(null);
    const [logoUploading, setLogoUploading] = useState(false);
    const [logoMessage, setLogoMessage] = useState(null);
    const [members, setMembers] = useState([]);
    const [membersLoading, setMembersLoading] = useState(false);
    const [membersError, setMembersError] = useState(null);
    const [memberActionMessage, setMemberActionMessage] = useState(null);
    const [memberActionError, setMemberActionError] = useState(null);
    const [newMemberId, setNewMemberId] = useState('');
    const [auditEntries, setAuditEntries] = useState([]);
    const [auditLoading, setAuditLoading] = useState(false);
    const [auditError, setAuditError] = useState(null);
    const [selfProfile, setSelfProfile] = useState(null);
    const [teamDeleteBusy, setTeamDeleteBusy] = useState(false);

    const isAllowed = MangaApiService.isAllowedDomain();
    const hasSession = !!localStorage.getItem('sessionKey');
    const teamId = getTeamId(team);
    const selfId = selfProfile?.data?.id || selfProfile?.id || localStorage.getItem('profileId');
    const isModerator = isRoleActive(selfProfile?.data?.mod ?? selfProfile?.mod ?? localStorage.getItem('mod'));
    const isEditor = isRoleActive(selfProfile?.data?.editor ?? selfProfile?.editor ?? localStorage.getItem('editor'));
    const isOwner = members.some((member) => {
        const memberId = member?.userId || member?.id || member?.uid;
        const role = String(member?.role || '').toUpperCase();
        return memberId && selfId && String(memberId) === String(selfId) && role === 'OWNER';
    });
    const canManageMembers = isOwner || isModerator || isEditor;

    useEffect(() => {
        document.title = 'animix - Команда';
    }, []);

    const loadTeam = async () => {
        if (!hasSession || !isAllowed) {
            return;
        }
        setTeamLoading(true);
        setTeamError(null);
        try {
            const response = await MangaApiService.getMyTeam();
            const payload = unwrapResponse(response);
            if (payload) {
                setTeam(payload);
                setTeamForm({
                    name: payload.name || payload.title || '',
                    description: payload.description || '',
                    donationLinks: joinLinks(
                        payload.donationLinks || payload.donateLinks || payload.donations || []
                    ),
                    logoFileId: payload.logoFileId || payload.logo_file_id || payload.logo?.fileId || '',
                    logoStorageBucket: payload.logoStorageBucket || payload.logo_storage_bucket || payload.logo?.bucket || '',
                });
            } else {
                setTeam(null);
                setTeamForm(emptyTeamForm);
            }
        } catch (err) {
            if (err?.response?.status === 404) {
                setTeam(null);
                setTeamForm(emptyTeamForm);
            } else {
                setTeamError(getMangaErrorMessage(err, 'Не удалось загрузить команду.'));
            }
        } finally {
            setTeamLoading(false);
        }
    };

    useEffect(() => {
        if (!isAllowed || !hasSession) {
            return;
        }
        loadTeam();
    }, [isAllowed, hasSession]);

    useEffect(() => {
        if (!hasSession) {
            setSelfProfile(null);
            return;
        }
        let isActive = true;
        const loadSelf = async () => {
            try {
                const response = await ApiService.getSelf();
                if (isActive) {
                    setSelfProfile(response?.data || response);
                }
            } catch (err) {
                if (isActive) {
                    setSelfProfile(null);
                }
            }
        };
        loadSelf();
        return () => {
            isActive = false;
        };
    }, [hasSession]);

    const loadMembers = async (id) => {
        if (!id) {
            setMembers([]);
            return;
        }
        setMembersLoading(true);
        setMembersError(null);
        try {
            const response = await MangaApiService.getTeamMembers(id);
            setMembers(unwrapList(response));
        } catch (err) {
            setMembersError(getMangaErrorMessage(err, 'Не удалось загрузить участников.'));
            setMembers([]);
        } finally {
            setMembersLoading(false);
        }
    };

    const loadAudit = async (id) => {
        if (!id) {
            setAuditEntries([]);
            return;
        }
        setAuditLoading(true);
        setAuditError(null);
        try {
            const response = await MangaApiService.getTeamAudit(id);
            setAuditEntries(unwrapList(response));
        } catch (err) {
            const status = err?.response?.status;
            if (status === 403) {
                setAuditEntries([]);
                setAuditError('Нет доступа к аудиту команды.');
            } else {
                setAuditError(getMangaErrorMessage(err, 'Не удалось загрузить аудит.'));
            }
        } finally {
            setAuditLoading(false);
        }
    };

    useEffect(() => {
        if (!teamId || !hasSession) {
            setMembers([]);
            setAuditEntries([]);
            return;
        }
        loadMembers(teamId);
        loadAudit(teamId);
    }, [teamId, hasSession]);

    const handleLogoUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }
        if (file.size > LOGO_MAX_SIZE_BYTES) {
            setTeamError('Логотип слишком большой. Максимум 3 МБ.');
            setLogoMessage(null);
            event.target.value = '';
            return;
        }
        setLogoMessage(null);
        setTeamError(null);
        setLogoUploading(true);
        try {
            const response = await MangaApiService.uploadFile(file);
            const uploaded = extractUploadMeta(response, file.name);
            if (!uploaded.fileId || !uploaded.bucket) {
                throw new Error('Не удалось получить данные файла.');
            }
            setTeamForm((prev) => ({
                ...prev,
                logoFileId: uploaded.fileId,
                logoStorageBucket: uploaded.bucket,
            }));
            setLogoMessage('Логотип загружен. Не забудьте сохранить команду.');
        } catch (err) {
            setTeamError(getImageUploadErrorMessage(err, 'Не удалось загрузить логотип.'));
        } finally {
            setLogoUploading(false);
            event.target.value = '';
        }
    };

    const handleSaveTeam = async () => {
        setTeamMessage(null);
        setTeamError(null);
        if (!hasSession) {
            setTeamError('Войдите, чтобы управлять командой.');
            return;
        }
        const name = teamForm.name.trim();
        if (!name) {
            setTeamError('Укажите название команды.');
            return;
        }
        if (name.length > TEAM_NAME_LIMIT) {
            setTeamError(`Название команды не может быть длиннее ${TEAM_NAME_LIMIT} символов.`);
            return;
        }
        const donationLinks = splitLinks(teamForm.donationLinks);
        const donationKey = resolveDonationKey(team);
        const payload = {
            name,
            ...(teamForm.description.trim() ? { description: teamForm.description.trim() } : {}),
            ...(teamForm.logoFileId ? { logoFileId: teamForm.logoFileId } : {}),
            ...(teamForm.logoStorageBucket ? { logoStorageBucket: teamForm.logoStorageBucket } : {}),
            ...(donationLinks.length > 0 ? { [donationKey]: donationLinks } : {}),
        };
        try {
            const response = team
                ? await MangaApiService.updateTeam(teamId, payload)
                : await MangaApiService.createTeam(payload);
            const payloadData = unwrapResponse(response);
            setTeam(payloadData || team);
            setTeamMessage(team ? 'Команда обновлена.' : 'Команда создана и отправлена на модерацию.');
            if (!team && payloadData) {
                setTeamForm((prev) => ({
                    ...prev,
                    name: payloadData.name || prev.name,
                    description: payloadData.description || prev.description,
                }));
            }
        } catch (err) {
            setTeamError(getMangaErrorMessage(err, 'Не удалось сохранить команду.'));
        }
    };

    const handleAddMember = async () => {
        setMemberActionMessage(null);
        setMemberActionError(null);
        if (!newMemberId.trim()) {
            setMemberActionError('Укажите ID пользователя.');
            return;
        }
        if (!canManageMembers) {
            setMemberActionError('Добавление доступно только владельцу команды или модератору.');
            return;
        }
        try {
            await MangaApiService.addTeamMember(teamId, newMemberId.trim());
            setMemberActionMessage('Участник добавлен.');
            setNewMemberId('');
            await loadMembers(teamId);
            await loadAudit(teamId);
        } catch (err) {
            setMemberActionError(getMangaErrorMessage(err, 'Не удалось добавить участника.'));
        }
    };

    const handleRemoveMember = async (memberId) => {
        setMemberActionMessage(null);
        setMemberActionError(null);
        if (!canManageMembers) {
            setMemberActionError('Удаление доступно только владельцу команды или модератору.');
            return;
        }
        const confirmRemove = window.confirm('Удалить участника из команды?');
        if (!confirmRemove) {
            return;
        }
        try {
            await MangaApiService.removeTeamMember(teamId, memberId);
            setMemberActionMessage('Участник удален.');
            await loadMembers(teamId);
            await loadAudit(teamId);
        } catch (err) {
            setMemberActionError(getMangaErrorMessage(err, 'Не удалось удалить участника.'));
        }
    };

    const handleTransferOwnership = async (memberId) => {
        setMemberActionMessage(null);
        setMemberActionError(null);
        if (!canManageMembers) {
            setMemberActionError('Передача доступна только владельцу команды или модератору.');
            return;
        }
        const confirmTransfer = window.confirm('Передать владение командой?');
        if (!confirmTransfer) {
            return;
        }
        try {
            await MangaApiService.transferTeamOwnership(teamId, memberId);
            setMemberActionMessage('Владелец команды обновлен.');
            await loadMembers(teamId);
            await loadTeam();
            await loadAudit(teamId);
        } catch (err) {
            setMemberActionError(getMangaErrorMessage(err, 'Не удалось передать владение.'));
        }
    };

    const handleLeaveTeam = async () => {
        setMemberActionMessage(null);
        setMemberActionError(null);
        if (isOwner) {
            setMemberActionError('Владелец не может покинуть команду.');
            return;
        }
        const confirmLeave = window.confirm('Покинуть команду?');
        if (!confirmLeave) {
            return;
        }
        try {
            await MangaApiService.leaveTeam();
            setMemberActionMessage('Вы вышли из команды.');
            await loadTeam();
            setMembers([]);
        } catch (err) {
            setMemberActionError(getMangaErrorMessage(err, 'Не удалось покинуть команду.'));
        }
    };

    const handleDeleteTeam = async () => {
        setTeamMessage(null);
        setTeamError(null);
        if (!hasSession) {
            setTeamError('Войдите, чтобы удалить команду.');
            return;
        }
        if (!teamId) {
            setTeamError('Команда не найдена.');
            return;
        }
        if (!isOwner) {
            setTeamError('Удаление доступно только владельцу команды.');
            return;
        }
        const confirmDelete = window.confirm(
            'Удалить команду и все связанные тайтлы/главы/страницы? Это необратимо.'
        );
        if (!confirmDelete) {
            return;
        }
        setTeamDeleteBusy(true);
        try {
            await MangaApiService.deleteTeam(teamId);
            setTeamMessage('Команда удалена.');
            setTeam(null);
            setTeamForm(emptyTeamForm);
            setMembers([]);
            setAuditEntries([]);
            setNewMemberId('');
        } catch (err) {
            setTeamError(getMangaErrorMessage(err, 'Не удалось удалить команду.'));
        } finally {
            setTeamDeleteBusy(false);
        }
    };

    const teamStatus = team?.status || team?.state || team?.moderationStatus;
    const teamStatusLabel = translateStatus(teamStatus);
    const teamRejectionReason = getRejectionReason(team);

    if (!isAllowed) {
        return (
            <PageShell>
                <MangaAccessNotice />
            </PageShell>
        );
    }

    return (
        <PageShell className="manga-studio-page">
            <PageHeader title="Команда" eyebrow="Студия манги" />
            <MangaStudioNav />

            {!hasSession ? (
                <div className="manga-inline-warning">Войдите, чтобы создавать и редактировать команды.</div>
            ) : null}
            {teamError ? <div className="manga-inline-warning">{teamError}</div> : null}

            <div className="studio-layout">
                <aside className="studio-layout__side">
                    {/* Who the team is and who is in it. */}
                    <div className="manga-panel">
                        {teamLoading ? <div className="loader" /> : null}
                        {team ? (
                            <div className="manga-studio-summary">
                                <div className="manga-studio-summary-icon"><img src={teamIcon} alt="" aria-hidden="true" /></div>
                                <div>
                                    <div className="manga-studio-summary-title">{team.name || team.title || 'Команда'}</div>
                                    {teamStatus ? <span className="studio-status">{teamStatusLabel}</span> : null}
                                </div>
                            </div>
                        ) : (
                            <div className="manga-help">
                                Команды пока нет. Заполните профиль — после создания она попадёт на модерацию.
                            </div>
                        )}
                        {isRejectedStatus(teamStatus) && teamRejectionReason ? (
                            <div className="manga-inline-warning">Причина отклонения: {teamRejectionReason}</div>
                        ) : null}

                        <div className="studio-card__head studio-card__head--inner">
                            <h3>Участники</h3>
                        </div>
                        {membersLoading ? <div className="loader" /> : null}
                        {membersError ? <div className="manga-inline-warning">{membersError}</div> : null}
                        {memberActionError ? <div className="manga-inline-warning">{memberActionError}</div> : null}
                        {memberActionMessage ? <div className="manga-inline-note">{memberActionMessage}</div> : null}
                        {canManageMembers && teamId ? (
                            <div className="manga-field-row">
                                <input
                                    type="text"
                                    placeholder="ID пользователя или UUID"
                                    value={newMemberId}
                                    onChange={(event) => setNewMemberId(event.target.value)}
                                    disabled={!hasSession || !teamId || !canManageMembers}
                                />
                                <button type="button" className="button-sec" onClick={handleAddMember} disabled={!hasSession || !teamId || !canManageMembers}>
                                    Добавить
                                </button>
                            </div>
                        ) : null}
                        {members.length > 0 ? (
                            <div className="manga-team-members">
                                {members.map((member) => {
                                    const memberId = member?.userId || member?.id || member?.uid;
                                    const role = String(member?.role || '').toUpperCase();
                                    const nick = member?.nick || 'Ник не указан';
                                    const canModify = canManageMembers && role !== 'OWNER';
                                    return (
                                        <div key={memberId || nick} className="manga-team-member">
                                            <div className="manga-team-member-name">{nick}</div>
                                            <div className="manga-team-member-role">
                                                {role ? translateTeamRole(role) : ''}{memberId ? ` · ID ${memberId}` : ''}
                                            </div>
                                            {canModify ? (
                                                <div className="manga-team-member-actions">
                                                    <button type="button" className="button-sec" onClick={() => handleTransferOwnership(memberId)} disabled={!memberId}>
                                                        Передать владение
                                                    </button>
                                                    <button type="button" className="manga-danger" onClick={() => handleRemoveMember(memberId)} disabled={!memberId}>
                                                        Удалить
                                                    </button>
                                                </div>
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="manga-empty">Участников пока нет.</div>
                        )}
                        {hasSession && teamId && !isOwner ? (
                            <button type="button" className="button-sec" onClick={handleLeaveTeam}>Покинуть команду</button>
                        ) : null}
                    </div>
                </aside>

                <div className="studio-layout__main">
                    {/* Profile form: the only place where the team is edited. */}
                    <div className="manga-panel">
                        <div className="studio-card__head">
                            <h2>{team ? 'Профиль команды' : 'Новая команда'}</h2>
                        </div>
                        <label className="manga-field">
                            <span>Название команды</span>
                            <input
                                type="text"
                                value={teamForm.name}
                                onChange={(event) => setTeamForm((prev) => ({ ...prev, name: event.target.value }))}
                                maxLength={TEAM_NAME_LIMIT}
                                disabled={!hasSession}
                            />
                        </label>
                        <label className="manga-field">
                            <span>Описание</span>
                            <textarea
                                placeholder="Чем занимаетесь, какой стиль переводов"
                                value={teamForm.description}
                                onChange={(event) => setTeamForm((prev) => ({ ...prev, description: event.target.value }))}
                                disabled={!hasSession}
                            />
                        </label>
                        <label className="manga-field">
                            <span>Ссылки на донат</span>
                            <textarea
                                placeholder="Каждая ссылка с новой строки — только Boosty или Patreon"
                                value={teamForm.donationLinks}
                                onChange={(event) => setTeamForm((prev) => ({ ...prev, donationLinks: event.target.value }))}
                                disabled={!hasSession}
                            />
                        </label>
                        <div className="manga-field">
                            <span>Логотип</span>
                            <div className="manga-studio-toolbar">
                                <label className="upload-label">
                                    {logoUploading ? 'Загрузка логотипа...' : 'Загрузить логотип'}
                                    <input type="file" accept="image/*" onChange={handleLogoUpload} disabled={!hasSession || logoUploading} />
                                </label>
                                <span className="manga-help">JPG/PNG до 3 МБ, показывается в карточках команды.</span>
                            </div>
                            {logoMessage ? <div className="manga-inline-note">{logoMessage}</div> : null}
                        </div>
                        <div className="studio-card__footer">
                            <button onClick={handleSaveTeam} disabled={!hasSession}>
                                {team ? 'Сохранить изменения' : 'Создать команду'}
                            </button>
                            <span className="manga-help">
                                {team ? 'Изменения отправят команду на повторную модерацию.' : 'После создания команда попадёт на модерацию.'}
                            </span>
                        </div>
                        {teamMessage ? <div className="manga-inline-note">{teamMessage}</div> : null}
                    </div>

                    {hasSession && teamId ? (
                        <details className="manga-panel studio-details">
                            <summary>Аудит за 7 дней</summary>
                            {auditLoading ? <div className="loader" /> : null}
                            {auditError ? <div className="manga-inline-warning">{auditError}</div> : null}
                            {auditEntries.length > 0 ? (
                                <div className="manga-audit-list">
                                    {auditEntries.map((entry) => {
                                        const createdAt = entry?.createdAt || entry?.created_at;
                                        const date = createdAt ? new Date(createdAt).toLocaleString() : '';
                                        return (
                                            <div key={entry?.id || `${entry?.action}-${createdAt}`} className="manga-audit-item">
                                                <div className="manga-audit-title">{translateAuditAction(entry?.action)}</div>
                                                <div className="manga-audit-meta">
                                                    {entry?.actorId ? `Пользователь ${entry.actorId}` : ''}{entry?.actorId && date ? ' · ' : ''}{date}
                                                </div>
                                                {entry?.details ? <div className="manga-audit-details">{formatAuditDetails(entry.details)}</div> : null}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="manga-empty">Аудит пока пуст.</div>
                            )}
                        </details>
                    ) : null}

                    {hasSession && teamId && isOwner ? (
                        <details className="manga-panel studio-details studio-details--danger">
                            <summary>Удаление команды</summary>
                            <div className="manga-help">Удаление команды удалит все тайтлы, главы и страницы без возможности восстановления.</div>
                            <button type="button" className="manga-danger" onClick={handleDeleteTeam} disabled={teamDeleteBusy}>
                                {teamDeleteBusy ? 'Удаление...' : 'Удалить команду'}
                            </button>
                        </details>
                    ) : null}
                </div>
            </div>
        </PageShell>
    );
};

export default MangaStudioTeamPage;
